import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { api, apiPublic } from '@/plugins/axios.js'
import { vue } from '@/main.js'

/**
 * Retorna os dados por equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getDadosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await api.get(
      `/app/vacina-tetra-viral/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os dados por microárea
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 * @param {string} microarea
 *
 * @returns {Array}
 */
export async function getDadosPorMicroarea(cod_ibge, nu_ine, microarea) {
  try {
    const { data } = await api.get(
      `/app/vacina-tetra-viral/${cod_ibge}/equipe/${nu_ine}/microarea/${microarea}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Formata o array de dados com as doses
 *
 * @param {*} cadastros
 * @returns {Array}
 */
export function formatarCadastrosComDoses(cadastros) {
  return cadastros.map((item, index) => {
    const vacinasPendentes = JSON.parse(item.vacinas_pendentes)
    const vacinas = {
      scr: JSON.parse(item.scr) ?? [],
    }

    const vacinaConfig = [
      {
        nome: 'coresTetraViral',
        vacina: 'scr',
        id_mn: '56',
        doses: [{ label: 'ÚNICA', id_ds: '9', idade: '15 meses' }],
      },
    ]

    const resultadoVacinas = vacinaConfig.reduce((acc, config) => {
      acc[config.nome] = processarDoses(
        vacinas[config.vacina],
        config.id_mn,
        config.doses,
        vacinasPendentes
      )
      return acc
    }, {})

    return {
      id: index,
      ...item,
      ...resultadoVacinas,
    }
  })
}

/**
 * Processa as doses configuradas de uma vacina.
 * @param {Object} vacina Dados da vacina
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {Array} dosesConfig Configuração das doses
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Array} Lista de doses processadas
 */
function processarDoses(vacina, id_mn, dosesConfig, vacinasPendentes) {
  return dosesConfig.map(({ label, id_ds, idade }) => {
    const dose = encontrarDose(vacina, id_mn, id_ds, vacinasPendentes)

    return {
      dose: label,
      cor: dose.cor,
      tag: dose.tag || '',
      id_ds: dose.id_ds || '',
      id_mn: dose.id_mn || '',
      dt_plc: dose.tipo === 'aplicada' ? dose.dt_plc : idade,
      dt_plc_br: dose.tipo === 'aplicada' ? dose.dt_plc_br : idade,
      orig: dose.orig || '',
      lt: dose.lt || '',
    }
  })
}

/**
 * Localiza a dose aplicada ou pendente de uma vacina.
 * @param {Array} doses Lista de doses aplicadas
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {String|Number} id_ds Identificador da dose
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Object} Dados da dose encontrada ou padrão
 */
function encontrarDose(doses, id_mn, id_ds, vacinasPendentes) {
  const aplicada = doses.find((el) => el.id_ds === id_ds && el.id_mn === id_mn)
  if (aplicada) {
    return {
      tipo: 'aplicada',
      cor: 'green',
      ...aplicada,
      dt_plc_br: aplicada.dt_plc?.split('-').reverse().join('/') || '',
    }
  }

  const pendente = vacinasPendentes?.find(
    (el) => el.id_ds === id_ds && el.id_mn === id_mn && el.na == '0'
  )
  if (pendente) {
    return {
      tipo: 'pendente',
      cor: 'red',
      ...pendente,
      dt_plc_br: '',
      orig: pendente.orig || '',
      lt: pendente.lt || '',
    }
  }

  return {
    tipo: 'pendente',
    id_ds: id_ds,
    id_mn: id_mn,
    cor: 'white',
    orig: '',
    lt: '',
  }
}

/**
 * Filtra os registros classificados como vermelhos.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVermelhos(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresTetraViral']

  return cadastrosFormatados.filter((item) =>
    camposCores.some(
      (campo) =>
        Array.isArray(item[campo]) &&
        item[campo].some((dose) => dose.cor === 'red')
    )
  )
}

/**
 * Filtra os registros classificados como verdes.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVerdes(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresTetraViral']

  const coresPermitidas = new Set(['green', 'white'])

  return cadastrosFormatados.filter((item) =>
    camposCores.every((campo) => {
      const arr = item[campo]
      return (
        Array.isArray(arr) && arr.every((el) => coresPermitidas.has(el.cor))
      )
    })
  )
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 */
export function generatePdf(cadastros, selectEquipe, microArea) {
  let title_pdf = 'Tetra viral'
  let titlePdfSave = utils.methods.titlePdf(
    'Tetra viral',
    selectEquipe.no_equipe
  )

  let description_pdf = 'Situação vacinal de crianças com Tetra viral'
  let headers_pdf = [
    'NOME',
    'NASCIMENTO',
    'MÃE',
    'MA',
    'TETRA VIRAL',
    'DOCUMENTO',
  ]
  var data_pdf = []
  let doses = formatarCadastrosComDoses(cadastros)
  for (let i = 0; i < doses.length; i++) {
    let obj = {}
    obj.no_cidadao = doses[i].no_cidadao
    obj.dt_nascimento = utils.methods.formatDate(doses[i].dt_nascimento)
    obj.no_mae = doses[i].no_mae
    obj.ma = doses[i].micro_area
    obj.tetra_viral = ''
    doses[i].coresTetraViral.forEach((item) => {
      obj.tetra_viral += `${item.dose} - ${item.dt_plc_br} \n`
    })
    let documentoText = ''
    let lineBreak = ''
    if (cadastros[i].nu_cpf !== null && cadastros[i].nu_cpf !== undefined) {
      lineBreak = '\n'
      documentoText += `CPF ${cadastros[i].nu_cpf}`
    }
    if (cadastros[i].nu_cns !== null && cadastros[i].nu_cns !== undefined) {
      documentoText += `${lineBreak}CNS ${cadastros[i].nu_cns}`
    }
    obj.documento = documentoText
    data_pdf.push(obj)
  }
  let doc = new jsPDF(utils.methods.encryptionPdf())
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)
  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf
      )
      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Equipe',
            value: selectEquipe.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe.ine_esf_eap,
          },
          {
            label: 'Microárea',
            value: microArea,
          },
        ],
        verticalPosition
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY)

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

export const dataFields = {
  Nome: 'no_cidadao',
  Nascimento: {
    field: 'dt_nascimento',
    callback: (value) => {
      return value !== null ? utils.methods.formatDate(value) : ''
    },
  },
  Mãe: 'no_mae',
  MA: 'micro_area',
}

export const filterIdadeOptions = [
  { label: '11 - 12 anos', value: '11 - 12 anos' },
  { label: '13 - 14 anos', value: '13 - 14 anos' },
]

export function handleFilterIdade(data, filter) {
  let filtrado = data.filter((item) => {
    if (filter === null) {
      return item
    }
    if (filter === '11 - 12 anos') {
      return Number(item.idade_anos) >= 11 && Number(item.idade_anos) <= 12
    } else if (filter === '13 - 14 anos') {
      return Number(item.idade_anos) >= 13 && Number(item.idade_anos) <= 14
    } else {
      return item
    }
  })
  return filtrado
}
